import { useEffect, useState } from "react";
import { Bell, MapPin, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isPushSupported, subscribeToPush } from "@/lib/push";

// We now re-prompt every session if the user hasn't actually enabled
// notifications yet (per client request: "must always be open"). The
// dismiss button only silences the sheet for the current session.
const SESSION_DISMISS_KEY = "khanaqin_perms_dismissed_session";

/**
 * On every app load, show a bottom-sheet inviting the user to enable
 * notifications and location — unless notifications are already granted
 * at the OS level. Users can tap "لاحقاً" to hide it for this session
 * only; on the next visit it re-appears until they enable notifications.
 */
export default function PermissionsPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let mounted = true;
    const timer = setTimeout(() => {
      try {
        if (typeof window === "undefined") return;
        // Skip if user dismissed *in this session* only
        if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") return;
        // Skip if notifications are already granted or explicitly denied at OS level
        const notifState = typeof Notification !== "undefined" ? Notification.permission : "unsupported";
        if (notifState === "granted") return;
        if (mounted) setVisible(true);
      } catch {
        /* ignore */
      }
    }, 1200);
    return () => { mounted = false; clearTimeout(timer); };
  }, []);

  const dismissSession = () => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  const handleAllow = async () => {
    // Request notification permission
    try {
      if (isPushSupported() && typeof Notification !== "undefined") {
        const perm = await Notification.requestPermission();
        if (perm === "granted") {
          // Try to register push subscription (best-effort)
          try { await subscribeToPush(); } catch { /* ignore */ }
        }
      }
    } catch { /* ignore */ }

    // Request location permission (this shows the OS prompt)
    try {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          () => { /* success */ },
          () => { /* denied — that's OK */ },
          { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
        );
      }
    } catch { /* ignore */ }

    dismissSession();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[110] p-4 pointer-events-none"
      data-testid="permissions-prompt"
    >
      <div className="mx-auto max-w-md pointer-events-auto">
        <div className="relative rounded-[26px] p-5 shadow-2xl border border-primary/25 backdrop-blur-xl fade-up" style={{
          background: "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(7,11,25,0.98))"
        }}>
          {/* Ambient glow */}
          <div className="pointer-events-none absolute -top-16 -end-16 size-40 rounded-full bg-primary/25 blur-3xl" />
          {/* Close */}
          <button
            type="button"
            onClick={dismissSession}
            data-testid="perms-dismiss-btn"
            className="absolute top-3 end-3 size-8 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center transition"
            aria-label="إغلاق"
          >
            <X className="size-4" />
          </button>

          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <div className="size-9 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center">
                <Bell className="size-5 text-primary" />
              </div>
              <div className="size-9 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center">
                <MapPin className="size-5 text-primary" />
              </div>
            </div>
            <h3 className="text-white font-black text-lg leading-tight mb-1">
              فعّل الإشعارات والموقع للحصول على تجربة أفضل
            </h3>
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              الإشعارات: أخبار المدينة وتنبيهات الطلبات فوراً.
              <br />
              الموقع: عرض المحلات الأقرب إليك تلقائياً.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={dismissSession}
                className="h-11 rounded-full border-white/15 bg-transparent text-white hover:bg-white/10"
                data-testid="perms-later-btn"
              >
                لاحقاً
              </Button>
              <Button
                onClick={handleAllow}
                className="h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold shadow-lg glow-primary-soft"
                data-testid="perms-allow-btn"
              >
                تفعيل الآن
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
